import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, PROFILE_OPTIONS, type Account, type AccountPasswordResult } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { useTheme } from "../contexts/ThemeContext";
import { MarketsTab } from "./rights/MarketsTab";

type Props = {
  account: Account;
  /** Mot de passe fraîchement généré pour CE compte, à afficher une fois (sinon null). */
  revealed: AccountPasswordResult | null;
  onReveal: (result: AccountPasswordResult) => void;
  onDismissReveal: () => void;
  onEdit: () => void;
};

/**
 * Détail d'un compte : en-tête (identité, badge V2, action mot de passe avec
 * confirmation inline, bandeau d'affichage unique du mot de passe), puis la grande
 * zone des droits marchés. Aucune popup. (Le droit forex par paire a été abandonné
 * le 16/07/2026 : tout compte a accès au forex.)
 */
export function AccountDetail({ account, revealed, onReveal, onDismissReveal, onEdit }: Props) {
  const { t } = useTheme();
  const queryClient = useQueryClient();

  const [confirmingReset, setConfirmingReset] = useState(false);
  const [copied, setCopied] = useState(false);

  const resetMut = useMutation({
    mutationFn: () => api.accounts.resetPassword(account.idUserAPI),
    onSuccess: (result) => {
      setConfirmingReset(false);
      setCopied(false);
      onReveal(result);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });

  const profileMut = useMutation({
    mutationFn: (profile: string | null) => api.accounts.setProfile(account.idUserAPI, profile),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });

  const handleCopy = async () => {
    if (!revealed) return;
    await navigator.clipboard.writeText(revealed.password);
    setCopied(true);
  };

  const handleCancelReset = () => {
    setConfirmingReset(false);
    resetMut.reset();
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* EN-TÊTE : identité + action mot de passe */}
      <div style={{ padding: "18px 22px 0", flex: "none" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: t.text1 }}>{account.nom}</span>
              <span style={{
                padding: "2px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
                background: account.hasV2Access ? t.doneBg : t.tagBg,
                color: account.hasV2Access ? t.doneColor : t.tagColor,
              }}>
                {account.hasV2Access ? "V2 actif" : "Ancienne API"}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: t.text2, marginTop: 3 }}>
              Login <b style={{ color: t.text1 }}>{account.login}</b>
              {" · "}Société{" "}
              <b style={{ color: t.text1 }}>{account.nomEntreprise ?? "non rattachée"}</b>
              <span title={`Id société (référentiel maître) : ${account.idEntreprise}`}>
                {" · "}id compte {account.idUserAPI}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: t.text2, marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
              <span title="Ouvre les endpoints réservés de WsdApiV2 (voir API-AGRIMARKET.md)">Profil d'accès</span>
              <select
                value={account.profile ?? ""}
                disabled={profileMut.isPending}
                onChange={e => profileMut.mutate(e.target.value === "" ? null : e.target.value)}
                style={{
                  padding: "3px 8px", borderRadius: 6, fontSize: 12.5, fontFamily: "inherit",
                  border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text1,
                  cursor: profileMut.isPending ? "wait" : "pointer",
                }}
              >
                {PROFILE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {profileMut.isError && (
                <span style={{ color: t.lateColor }}>
                  {profileMut.error instanceof Error ? profileMut.error.message : "Erreur"}
                </span>
              )}
            </div>
          </div>
          {/* Modification reservee aux comptes V2 : un compte de l'ancienne API n'est pas
              gere ici, et l'ecrire lui ferait perdre son rattachement historique. Le
              serveur refuse aussi, l'absence de bouton n'est que le premier garde-fou. */}
          {!confirmingReset && account.hasV2Access && (
            <button onClick={onEdit} style={{
              border: `1px solid ${t.rowBorder}`, background: t.panelBg, color: t.accent,
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
            }}>
              Modifier
            </button>
          )}
          {!confirmingReset && (
            <button onClick={() => setConfirmingReset(true)} style={{
              border: `1px solid ${t.rowBorder}`, background: t.panelBg, color: t.accent,
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
            }}>
              {account.hasV2Access ? "Régénérer le mot de passe" : "Activer sur la V2"}
            </button>
          )}
        </div>

        {/* Confirmation inline (pas de popup) */}
        {confirmingReset && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginTop: 12,
            background: t.panelBg, border: `1px solid ${t.rowBorder}`,
            borderRadius: 10, padding: "10px 14px",
          }}>
            <span style={{ flex: 1, fontSize: 13, color: t.text2 }}>
              {account.hasV2Access
                ? <>Un nouveau mot de passe va être généré pour <b>{account.login}</b>. L'ancien cessera de fonctionner immédiatement.</>
                : <>Un mot de passe V2 va être généré pour <b>{account.login}</b>. Son accès à l'ancienne API n'est pas modifié.</>}
            </span>
            <button onClick={handleCancelReset} style={{
              border: `1px solid ${t.rowBorder}`, background: t.cardBg, color: t.text2,
              borderRadius: 8, padding: "7px 12px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
            }}>Annuler</button>
            <button onClick={() => resetMut.mutate()} disabled={resetMut.isPending} style={{
              border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
              padding: "8px 14px", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
              cursor: resetMut.isPending ? "not-allowed" : "pointer", opacity: resetMut.isPending ? 0.7 : 1,
            }}>{resetMut.isPending ? "Génération..." : "Générer"}</button>
          </div>
        )}
        {resetMut.isError && (
          <div style={{ marginTop: 10, padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13 }}>
            {(resetMut.error as Error).message}
          </div>
        )}

        {/* Bandeau mot de passe : affiché UNE SEULE FOIS, jamais stocké */}
        {revealed && (
          <div style={{
            marginTop: 12, background: t.doneBg, border: `1px solid ${t.doneColor}`,
            borderRadius: 10, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 12.5, color: t.doneColor, fontWeight: 600, marginBottom: 6 }}>
              Mot de passe de « {revealed.login} », affiché une seule fois. Transmettez-le par un canal
              sûr puis fermez ce bandeau (il ne pourra plus être retrouvé, seulement régénéré).
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <code style={{ flex: 1, fontSize: 15, letterSpacing: .5, color: t.text1, wordBreak: "break-all" }}>
                {revealed.password}
              </code>
              <button onClick={handleCopy} style={{
                border: `1px solid ${t.doneColor}`, background: copied ? t.doneColor : "transparent",
                color: copied ? "#fff" : t.doneColor, borderRadius: 8, padding: "6px 12px",
                fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
              }}>{copied ? "Copié !" : "Copier"}</button>
              <button onClick={onDismissReveal} style={{
                border: "none", background: "transparent", color: t.doneColor,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
              }}>Fermer</button>
            </div>
          </div>
        )}
      </div>

      {/* DROITS MARCHÉS */}
      <div style={{
        padding: "14px 22px 10px", borderBottom: `1px solid ${t.divider}`, flex: "none",
        fontSize: 13.5, fontWeight: 600, color: t.accent,
      }}>
        Droits marchés
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 22px 22px" }}>
        <MarketsTab idUserAPI={account.idUserAPI} />
      </div>
    </div>
  );
}
